import { forwardRef, useRef, type Ref, Children } from 'react';

import { AccordionActions, useAccordionState } from './accordion-state.js';
import type { AccordionProps } from './accordion-types.js';
import { accordionDividerMarginCSS } from './Accordion.sty.js';
import { AccordionSection } from './AccordionSection.js';
import { AccordionSectionContent } from './AccordionSectionContent.js';
import { AccordionSectionLabel } from './AccordionSectionLabel.js';
import { AccordionContextProvider } from './contexts/AccordionContext.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { Flex } from '../../layouts/flex/Flex.js';

const COMPONENT_NAME = 'Accordion';

/**
 * Accordion component
 */
function RootAccordion<Multiple extends boolean = false>(
  props: AccordionProps<Multiple>,
  forwardedRef: Ref<HTMLDivElement>,
) {
  const {
    expanded,
    defaultExpanded,
    onExpandChange,
    multiple = false as Multiple,
    triggerPosition = 'end',
    interactive = true,
    color = 'neutral',
    showDividers = true,
    size = 'default',
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const sectionCount = Children.count(children);
  const accordionSections = Children.map(children, (child, index) => (
    <>
      {child}
      {showDividers && sectionCount > 1 && index < sectionCount - 1 && (
        <Divider className={accordionDividerMarginCSS[size]} color={color} />
      )}
    </>
  ));

  const [state, dispatch] = useAccordionState<Multiple>({
    expanded,
    defaultExpanded,
    multiple,
    onExpandChange,
  });

  // Syncronize the open elements if multiple changes.
  const lastMultiple = useRef(multiple);
  if (multiple !== lastMultiple.current) {
    dispatch({ type: AccordionActions.SET_MULTIPLE, payload: multiple });
    lastMultiple.current = multiple;
  }

  return (
    <div
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      <AccordionContextProvider
        value={{
          triggerPosition,
          size,
          interactive,
          state,
          color,
          dispatch,
        }}
      >
        <Flex
          flexDirection="column"
          gap={showDividers ? 0 : size === 'default' ? 8 : 4}
        >
          {accordionSections}
        </Flex>
      </AccordionContextProvider>
    </div>
  );
}

RootAccordion.displayName = COMPONENT_NAME;

const AccordionForwardRef = forwardRef(RootAccordion);

/**
 * The `Accordion` component can be used to group information, reveal and hide content.
 * @public
 */
export const Accordion = Object.assign(AccordionForwardRef, {
  Section: AccordionSection,
  SectionLabel: AccordionSectionLabel,
  SectionContent: AccordionSectionContent,
});
